import React, { useEffect, useState, useCallback } from 'react';

import {
  GoogleCredentialResponse,
  LoggingOptions,
  LoginOrRegData,
  ZRGlobal,
} from '@zr/common/js/google-sign-in.d';
import { loadAndRenderGoogleButton, loginOrRegisterWithGoogle } from '@zr/common/js/google-sign-in';
import './GoogleSignInButton.less';

declare const zr: ZRGlobal;

export interface GoogleSignInProps {
  /* pageType which will be sent to sign in XHR call for logging */
  pageType: string,
  /*
    Callback to run after a successful response from loginOrReg api call.
    e.g. To redirect the user to the nextUrl returned after login, write a onSuccessfulSignIn like:
    (jsonResponse: LoginOrRegData) => {
      window.location.href = jsonResponse.data.nextUrl;
    },

    To reload the entire page after sign in, the callback could look like:
    () => { window.location.reload() }
  */
  onSuccessfulSignIn: (jsonResponse: LoginOrRegData) => void,
  /* function to run while waiting for google login to complete. e.g. show a loading spinner here. */
  onIsLoading?: () => void,
  /* function to run while google login has finished. e.g. hide a loading spinner here. */
  onReady?: () => void,
  showBottomDivider?: boolean,
}

export const GoogleSignInButton: React.FC<GoogleSignInProps> = ({
  pageType, onSuccessfulSignIn, onIsLoading, onReady, showBottomDivider = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  // TODO: Use PageContext for zr globals
  const googleSignInClientId = zr?.page?.model?.GOOGLE_SIGNIN_CLIENT_ID;
  const LOGIN_OR = zr?.page?.translations?.LOGIN_OR ?? 'OR';

  // Code which is run after the user signs in. Make a call to our login api with the appropriate logging options,
  // then run the onSuccessfulSignIn callback if the sign in process goes well.
  const handleGoogleSignIn = useCallback((googleResponseObject: GoogleCredentialResponse) => {
    const loggingOptions: LoggingOptions = {
      regMethod: `google_reg_button.${pageType}`,
      loginMethod: `google_login_button.${pageType}`,
    };
    setIsLoading(true);

    loginOrRegisterWithGoogle(googleResponseObject, loggingOptions)
      .then((jsonResponse: LoginOrRegData) => {
        onSuccessfulSignIn(jsonResponse);
      }).catch((error) => {
        setIsLoading(false);
        throw error;
      });
  }, [onSuccessfulSignIn, pageType]);

  const googleRoot = `${pageType}_google_root`;
  // render the google sign in button after the initial component is rendered.
  // Provide the handleGoogleSign callback which runs after the user has successfully signed in.
  useEffect(() => {
    const googleSignInButton = document.getElementById(googleRoot);
    if (googleSignInButton) {
      loadAndRenderGoogleButton(googleSignInClientId, handleGoogleSignIn, googleSignInButton);
    }
  }, [googleSignInClientId, handleGoogleSignIn, googleRoot]);

  // disable all relevant inputs while loading sign in
  useEffect(() => {
    if (isLoading) {
      onIsLoading?.();
    } else {
      onReady?.();
    }
  }, [onIsLoading, onReady, isLoading]);

  return (
    <div className="GoogleSignInButton">
      <div id={googleRoot} className={isLoading ? 'hide' : ''} />

      <div id="loading_spinner" className={isLoading ? '' : 'hide'}>
        <svg className="zrs_spinner" width="66px" height="66px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle className="path" fill="none" strokeWidth="6" strokeLinecap="round" cx="33" cy="33" r="25" />
        </svg>
      </div>

      {showBottomDivider && <span className="divider">{LOGIN_OR}</span>}
    </div>
  );
};
